import { useState } from 'react'
import { clsx } from 'clsx'
import { useChartStore } from '../../store/chartStore'

interface ChartToolbarProps {
  onSnapshot: () => void
  onCopyToClipboard: () => void
  onShare: () => void
  onFullscreen: () => void
  isFullscreen: boolean
}

export function ChartToolbar({
  onSnapshot,
  onCopyToClipboard,
  onShare,
  onFullscreen,
  isFullscreen,
}: ChartToolbarProps) {
  const [showMenu, setShowMenu] = useState(false)
  const { priceScale, setPriceScale, autoScale, setAutoScale } = useChartStore()

  return (
    <div className="absolute top-4 right-4 flex items-center gap-2 z-20">
      {/* Price Scale Toggle */}
      <div className="flex items-center bg-kit-darker/80 backdrop-blur-sm rounded-lg border border-kit-border">
        <button
          onClick={() => setPriceScale('normal')}
          className={clsx(
            'px-2 py-1 text-xs font-medium transition-colors',
            priceScale === 'normal' ? 'text-kit-accent' : 'text-gray-400 hover:text-white'
          )}
          title="Linear Scale"
        >
          Lin
        </button>
        <button
          onClick={() => setPriceScale('log')}
          className={clsx(
            'px-2 py-1 text-xs font-medium transition-colors',
            priceScale === 'log' ? 'text-kit-accent' : 'text-gray-400 hover:text-white'
          )}
          title="Logarithmic Scale"
        >
          Log
        </button>
        <button
          onClick={() => setPriceScale('percentage')}
          className={clsx(
            'px-2 py-1 text-xs font-medium transition-colors',
            priceScale === 'percentage' ? 'text-kit-accent' : 'text-gray-400 hover:text-white'
          )}
          title="Percentage Scale"
        >
          %
        </button>
      </div>

      {/* Auto Scale Toggle */}
      <button
        onClick={() => setAutoScale(!autoScale)}
        className={clsx(
          'p-1.5 rounded-lg bg-kit-darker/80 backdrop-blur-sm border border-kit-border transition-colors',
          autoScale ? 'text-kit-accent' : 'text-gray-400 hover:text-white'
        )}
        title={autoScale ? 'Auto Scale On' : 'Auto Scale Off'}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      </button>

      {/* Screenshot */}
      <button
        onClick={onSnapshot}
        className="p-1.5 rounded-lg bg-kit-darker/80 backdrop-blur-sm border border-kit-border text-gray-400 hover:text-white transition-colors"
        title="Download Screenshot"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>

      {/* Copy to Clipboard */}
      <button
        onClick={onCopyToClipboard}
        className="p-1.5 rounded-lg bg-kit-darker/80 backdrop-blur-sm border border-kit-border text-gray-400 hover:text-white transition-colors"
        title="Copy to Clipboard"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
        </svg>
      </button>

      {/* Share */}
      <button
        onClick={onShare}
        className="p-1.5 rounded-lg bg-kit-darker/80 backdrop-blur-sm border border-kit-border text-gray-400 hover:text-white transition-colors"
        title="Share Chart"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
      </button>

      {/* Fullscreen */}
      <button
        onClick={onFullscreen}
        className="p-1.5 rounded-lg bg-kit-darker/80 backdrop-blur-sm border border-kit-border text-gray-400 hover:text-white transition-colors"
        title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
      >
        {isFullscreen ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        )}
      </button>

      {/* More Options */}
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-1.5 rounded-lg bg-kit-darker/80 backdrop-blur-sm border border-kit-border text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        </button>

        {showMenu && (
          <div className="absolute top-full right-0 mt-2 w-48 bg-kit-card border border-kit-border rounded-lg shadow-xl py-1 z-50">
            <button className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-kit-darker">
              Chart Settings
            </button>
            <button className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-kit-darker">
              Save Template
            </button>
            <button className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-kit-darker">
              Load Template
            </button>
            <div className="border-t border-kit-border my-1" />
            <button className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-kit-darker">
              Reset Chart
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
